// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { safeId } from '@/integrations/supabase/client';

export interface ProfileLite {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
  last_seen?: string | null;
}

export interface RelationshipRow {
  id: string;
  user_id: string;
  other_user_id: string;
  status: 'pending' | 'accepted' | 'blocked';
  requested_at: string;
  responded_at?: string | null;
  blocked_by?: string | null;
}

export const searchUsers = async (q: string, limit = 20): Promise<ProfileLite[]> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email, avatar_url, last_seen')
    .ilike('first_name', `%${q}%`)
    .limit(limit);
  if (error) throw error;
  return data || [];
};

export const listAllUsers = async (limit = 50, offset = 0): Promise<ProfileLite[]> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email, avatar_url, last_seen')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw error;
  return data || [];
};

export const getRelationships = async (): Promise<RelationshipRow[]> => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) return [] as any[];

  const { data, error } = await supabase
    .from('user_relationships')
    .select('*')
    .or(`user_id.eq.${me},other_user_id.eq.${me}`);
  if (error) throw error;
  return data || [];
};

export const sendFriendRequest = async (otherUserId: string) => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) throw new Error('Not authenticated');
  if (me === otherUserId) throw new Error('Cannot friend yourself');

  const { error } = await supabase
    .from('user_relationships')
    .insert({ user_id: me, other_user_id: otherUserId, status: 'pending' });
  if (error) throw error;
};

export const acceptFriendRequest = async (otherUserId: string) => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) throw new Error('Not authenticated');

  // Accept if there is a request where other is requester
  const { error } = await supabase
    .from('user_relationships')
    .update({ status: 'accepted', responded_at: new Date().toISOString() })
    .or(`and(user_id.eq.${otherUserId},other_user_id.eq.${me}),and(user_id.eq.${me},other_user_id.eq.${otherUserId})`)
    .neq('status', 'accepted');
  if (error) throw error;

  // Expand network
  await supabase.rpc('expand_network_for_pair', { a: me, b: otherUserId });
};

export const declineFriendRequest = async (otherUserId: string) => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) throw new Error('Not authenticated');
  // Delete pending where requester is other and recipient is me
  const { error } = await supabase
    .from('user_relationships')
    .delete()
    .match({ user_id: otherUserId, other_user_id: me, status: 'pending' });
  if (error) throw error;
};

export const cancelFriendRequest = async (otherUserId: string) => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) throw new Error('Not authenticated');
  // Delete pending where requester is me and recipient is other
  const { error } = await supabase
    .from('user_relationships')
    .delete()
    .match({ user_id: me, other_user_id: otherUserId, status: 'pending' });
  if (error) throw error;
};

export const listFriends = async (): Promise<string[]> => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) return [];

  const { data, error } = await supabase
    .from('user_relationships')
    .select('user_id, other_user_id, status')
    .eq('status', 'accepted')
    .or(`user_id.eq.${me},other_user_id.eq.${me}`);
  if (error) throw error;
  const ids = new Set<string>();
  (data || []).forEach((r) => {
    if (r.user_id === me) ids.add(r.other_user_id);
    else ids.add(r.user_id);
  });
  return Array.from(ids);
};

export const listFriendRequests = async () => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) return { incoming: [], outgoing: [] };

  const { data, error } = await supabase
    .from('user_relationships')
    .select('*')
    .neq('status', 'accepted')
    .or(`user_id.eq.${me},other_user_id.eq.${me}`);
  if (error) throw error;
  const incoming = (data || []).filter((r) => r.other_user_id === me && r.status === 'pending');
  const outgoing = (data || []).filter((r) => r.user_id === me && r.status === 'pending');
  return { incoming, outgoing };
};


